/**
 * Type definitions for Moodboard store
 * 
 * This file provides TypeScript interfaces for moodboard-related data
 * to ensure consistent typing throughout the application.
 */

/**
 * Raw moodboard item data as stored in the database
 */
export interface MoodboardItemData {
  id: string;
  topic_id: string;
  user_id: string;
  url: string;
  width: number;
  height: number;
  created_at: string;
  updated_at: string;
  metadata?: {
    client_id?: string;
    last_update?: string;
    [key: string]: any;
  };
  user_info?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
}

/**
 * Formatted moodboard item for use in the UI
 */
export interface FormattedMoodboardItem {
  id: string;
  topicId: string;
  userId: string;
  url: string;
  width: number;
  height: number;
  createdAt: string;
  updatedAt: string;
  userInfo?: {
    name: string | null;
    email: string | null;
    avatarUrl: string | null;
  };
}

/**
 * Input for creating a new moodboard item
 */
export interface CreateMoodboardItemInput {
  topicId: string;
  url: string;
  width: number;
  height: number;
}

/**
 * Input for updating an existing moodboard item
 */
export interface UpdateMoodboardItemInput {
  url?: string;
  width?: number;
  height?: number;
}

/**
 * Helper function to format a moodboard item for consumption in UI components
 */
export function formatMoodboardItem(item: MoodboardItemData): FormattedMoodboardItem {
  return {
    id: item.id,
    topicId: item.topic_id,
    userId: item.user_id,
    url: item.url,
    width: item.width,
    height: item.height,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    userInfo: item.user_info ? {
      name: item.user_info.name || null,
      email: item.user_info.email || null,
      avatarUrl: item.user_info.avatar_url || null
    } : undefined
  };
}

/**
 * Helper function to format a moodboard item for sending to the database
 */
export function formatMoodboardItemForDatabase(item: FormattedMoodboardItem): MoodboardItemData {
  return {
    id: item.id,
    topic_id: item.topicId,
    user_id: item.userId,
    url: item.url,
    width: item.width,
    height: item.height,
    created_at: item.createdAt,
    updated_at: item.updatedAt,
    user_info: item.userInfo ? {
      name: item.userInfo.name || undefined,
      email: item.userInfo.email || undefined,
      avatar_url: item.userInfo.avatarUrl || undefined
    } : undefined
  };
}